/*
  Rhino cart add-ons — rhinoskinsolutions.com (Motion theme)
  ---------------------------------------------------------
  Shows a compact "goes well with" list inside the cart drawer and on the cart page.

  How products are chosen (all in the browser, no app, no API keys):
    1. Read the cart (/cart.js). Seed products = the items in the cart, newest first,
       ignoring free-gift lines added by the Rhino gifts section (_rhino_gift property).
    2. Ask Shopify's Product Recommendations API for each seed:
         intent=complementary first (needs Search & Discovery rules; may be empty),
         then intent=related (always available).
    3. Category mix (optional): sort the picks into up to three collections (e.g. skin
       care / tools / apparel), top up thin groups from the collection itself, then deal
       them out in turn so the strip always shows a spread.
    4. If still short, fill from the fallback collection chosen in the section settings.
    5. Drop anything already in the cart, sold out, a free-gift product (optional), tagged
       as excluded, or priced at $0. Optionally order by product type priority.
  Adding: POST /cart/add.js, then ask Motion to rebuild the drawer (cart:build).
  If the theme does not rebuild within ~1.2s, the script rebuilds the cart lines,
  subtotal and header count itself from /cart?view=ajax.

  Version: 2026-09-25c (horizontal carousel, category mix)
*/
(function () {
  'use strict';

  if (window.__rcaLoaded) return;
  window.__rcaLoaded = true;

  var GIFT_PROP = '_rhino_gift';
  var CACHE_TTL = 10 * 60 * 1000;
  /* Use the untouched fetch that the gifts section saves, so a cart *read* never re-triggers its sync. */
  var quietFetch = window.__rgNativeFetch || window.fetch.bind(window);

  function esc(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function ss(action, key, value) {
    try {
      if (action === 'get') return window.sessionStorage.getItem(key);
      if (action === 'set') window.sessionStorage.setItem(key, value);
    } catch (e) {}
    return null;
  }

  function getJSON(url) {
    return quietFetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error(r.status); return r.json(); });
  }

  function cached(url) {
    var key = 'rca:' + url;
    var raw = ss('get', key);
    if (raw) {
      try {
        var hit = JSON.parse(raw);
        if (hit && hit.t && Date.now() - hit.t < CACHE_TTL) return Promise.resolve(hit.d);
      } catch (e) {}
    }
    return getJSON(url).then(function (d) { ss('set', key, JSON.stringify({ t: Date.now(), d: d })); return d; });
  }

  /* ---------- money ---------- */
  function money(cents, cfg) {
    if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function') {
      try { return theme.Currency.formatMoney(cents, cfg.moneyFormat); } catch (e) {}
    }
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: cfg.currency }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }

  /* ---------- product normalisation ----------
     The recommendations API and /collections/x/products.json shape products differently. */
  function toCents(p) {
    if (p == null) return 0;
    if (typeof p === 'number') return Math.round(p);
    var n = parseFloat(String(p).replace(/[^0-9.]/g, ''));
    return isNaN(n) ? 0 : Math.round(n * 100);
  }

  function imageFor(p) {
    var src = p.featured_image || (p.images && p.images[0] && (p.images[0].src || p.images[0])) || '';
    if (typeof src !== 'string') src = '';
    if (!src) return '';
    if (src.indexOf('//') === 0) src = 'https:' + src;
    var join = src.indexOf('?') > -1 ? '&' : '?';
    return src + join + 'width=360&height=360&crop=center';
  }

  function normalise(p) {
    var tags = Array.isArray(p.tags) ? p.tags : String(p.tags || '').split(',');
    tags = tags.map(function (t) { return String(t).trim().toLowerCase(); }).filter(Boolean);
    var variants = (p.variants || []).filter(function (v) { return v.available !== false; }).map(function (v) {
      return {
        id: v.id,
        title: v.title || 'Default Title',
        price: toCents(v.price),
        compare: toCents(v.compare_at_price)
      };
    });
    return {
      id: p.id,
      handle: p.handle,
      title: p.title,
      url: p.url || ('/products/' + p.handle),
      type: String(p.product_type || p.type || '').trim(),
      tags: tags,
      image: imageFor(p),
      variants: variants
    };
  }

  /* ---------- gift products to hide (from the Rhino gifts section config, if present) ---------- */
  function giftProductIds() {
    var ids = {};
    var el = document.querySelector('[data-rg-config]');
    if (!el) return ids;
    try {
      var cfg = JSON.parse(el.textContent);
      (cfg.tiers || []).forEach(function (t) { (t.products || []).forEach(function (p) { ids[p.id] = true; }); });
    } catch (e) {}
    return ids;
  }

  /* ---------- one block (drawer or page) ---------- */
  /* Settings come from the "Rhino cart add-ons" section in the header group. */
  function readConfig() {
    var el = document.querySelector('[data-rca-config]');
    if (!el) return null;
    var cfg;
    try { cfg = JSON.parse(el.textContent); } catch (e) { console.warn('[Rhino add-ons] bad config', e); return null; }
    cfg.excludeTags = (cfg.excludeTags || []).map(function (t) { return String(t).trim().toLowerCase(); }).filter(Boolean);
    cfg.priorityTypes = (cfg.priorityTypes || []).map(function (t) { return String(t).trim().toLowerCase(); }).filter(Boolean);
    cfg.limit = Math.max(1, Math.min(8, parseInt(cfg.limit, 10) || 4));
    cfg.mix = (cfg.mix || []).map(function (h) { return String(h).trim(); }).filter(Boolean);
    cfg.hideGifts = cfg.hideGifts !== false;
    return cfg;
  }

  function Block(root, cfg) {
    this.root = root;
    this.cfg = cfg;
    this.location = root.getAttribute('data-rca-location') || 'drawer';
    this.list = root.querySelector('[data-rca-list]');
    this.status = root.querySelector('[data-rca-status]');
    this.shown = [];
    this.token = 0;
    this.nav = root.querySelector('[data-rca-nav]');
    this.prev = root.querySelector('[data-rca-prev]');
    this.next = root.querySelector('[data-rca-next]');
    var title = root.querySelector('[data-rca-title]');
    if (title && cfg.heading) title.textContent = cfg.heading;
    this.bind();
  }

  /* ---------- horizontal strip: arrows + end states ---------- */
  Block.prototype.step = function () {
    var card = this.list.querySelector('[data-rca-item]');
    return card ? card.offsetWidth + 10 : 160;
  };

  Block.prototype.scrollByCards = function (dir) {
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    try { this.list.scrollBy({ left: dir * this.step(), behavior: reduce ? 'auto' : 'smooth' }); }
    catch (e) { this.list.scrollLeft += dir * this.step(); }
  };

  Block.prototype.updateNav = function () {
    if (!this.nav) return;
    var l = this.list;
    var overflow = l.scrollWidth > l.clientWidth + 4;
    this.nav.hidden = !overflow;
    if (!overflow) return;
    var max = l.scrollWidth - l.clientWidth;
    if (this.prev) this.prev.disabled = l.scrollLeft <= 2;
    if (this.next) this.next.disabled = l.scrollLeft >= max - 2;
  };

  Block.prototype.bind = function () {
    var self = this;
    if (this.prev) this.prev.addEventListener('click', function () { self.scrollByCards(-1); });
    if (this.next) this.next.addEventListener('click', function () { self.scrollByCards(1); });
    var ticking = false;
    this.list.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { ticking = false; self.updateNav(); });
    }, { passive: true });
    /* the drawer is display:none until opened, so widths are 0 at render time; re-check when it becomes visible or resizes */
    if ('ResizeObserver' in window) new ResizeObserver(function () { self.updateNav(); }).observe(this.list);
    window.addEventListener('resize', function () { self.updateNav(); });
    this.list.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-rca-add]');
      if (!btn) return;
      e.preventDefault();
      self.add(btn);
    });
    this.list.addEventListener('change', function (e) {
      var sel = e.target.closest('[data-rca-variant]');
      if (!sel) return;
      var item = sel.closest('[data-rca-item]');
      var opt = sel.options[sel.selectedIndex];
      var price = item.querySelector('[data-rca-price]');
      if (price && opt) price.innerHTML = self.priceHTML(parseInt(opt.getAttribute('data-price'), 10), parseInt(opt.getAttribute('data-compare'), 10));
      item.querySelector('[data-rca-add]').setAttribute('data-variant-id', opt.value);
    });
  };

  Block.prototype.say = function (text, isError) {
    if (!this.status) return;
    this.status.textContent = text || '';
    this.status.hidden = !text;
    this.status.classList.toggle('is-error', !!isError);
  };

  Block.prototype.priceHTML = function (price, compare) {
    var out = '';
    if (compare && compare > price) out += '<s>' + esc(money(compare, this.cfg)) + '</s>';
    out += esc(money(price, this.cfg));
    return out;
  };

  /* Build the candidate list for the current cart and render it. */
  Block.prototype.refresh = function (cart) {
    var self = this, cfg = this.cfg, token = ++this.token;
    if (!cart || !cart.items || !cart.items.length) { this.root.hidden = true; this.list.innerHTML = ''; this.shown = []; return Promise.resolve(); }

    var inCart = {};
    var seeds = [];
    cart.items.forEach(function (it) {
      inCart[it.product_id] = true;
      if (!(it.properties && it.properties[GIFT_PROP]) && seeds.indexOf(it.product_id) === -1) seeds.push(it.product_id);
    });
    seeds = seeds.slice(0, 3);
    var gifts = cfg.hideGifts ? giftProductIds() : {};

    var seen = {};
    /* Returns a normalised product if it may be suggested, else null. Marks it seen. */
    function accept(raw) {
      var p = normalise(raw);
      if (!p.id || seen[p.id] || inCart[p.id] || gifts[p.id]) return null;
      if (raw.available === false || !p.variants.length) return null;
      if (p.variants[0].price <= 0) return null;
      if (cfg.excludeTags.length && p.tags.some(function (t) { return cfg.excludeTags.indexOf(t) > -1; })) return null;
      seen[p.id] = true;
      return p;
    }

    var picked = [];               /* smart picks, in relevance order */
    var poolTarget = cfg.mix.length ? cfg.limit * 3 : cfg.limit + 2;
    function need() { return picked.length < poolTarget; }

    function recs(intent) {
      if (!seeds.length) return Promise.resolve();
      return Promise.all(seeds.map(function (id) {
        var url = cfg.routes.recommendations + '?product_id=' + id + '&limit=12&intent=' + intent;
        return cached(url).then(function (d) { return d && d.products ? d.products : []; }, function () { return []; });
      })).then(function (lists) {
        /* interleave so every cart item gets a say, newest item first */
        var i = 0, any = true;
        while (any && need()) {
          any = false;
          lists.forEach(function (l) { if (l[i]) { any = true; var p = accept(l[i]); if (p) picked.push(p); } });
          i++;
        }
      });
    }

    function collectionProducts(handle) {
      if (!handle) return Promise.resolve([]);
      var url = cfg.routes.collections + '/' + handle + '/products.json?limit=50';
      return cached(url).then(function (d) { return d && d.products ? d.products : []; }, function () { return []; });
    }

    /* Category mix: group the smart picks by collection, top up from the collection, deal out in turn. */
    function dealMix() {
      return Promise.all(cfg.mix.map(collectionProducts)).then(function (groupsRaw) {
        var members = groupsRaw.map(function (list) {
          var m = {}; list.forEach(function (p) { m[p.id] = true; }); return m;
        });
        var buckets = cfg.mix.map(function () { return []; });
        var other = [];
        picked.forEach(function (p) {
          var g = -1;
          for (var i = 0; i < members.length; i++) { if (members[i][p.id]) { g = i; break; } }
          if (g === -1) other.push(p); else buckets[g].push(p);
        });
        /* top up each group from its own collection (collection order; sold-out and in-cart skipped) */
        groupsRaw.forEach(function (list, i) {
          for (var k = 0; k < list.length && buckets[i].length < cfg.limit; k++) {
            var p = accept(list[k]); if (p) buckets[i].push(p);
          }
        });
        var out = [];
        var any = true;
        while (out.length < cfg.limit && any) {
          any = false;
          buckets.forEach(function (b) { if (b.length && out.length < cfg.limit) { out.push(b.shift()); any = true; } });
        }
        while (out.length < cfg.limit && other.length) out.push(other.shift());
        return out;
      });
    }

    var chain = Promise.resolve();
    if (cfg.intent === 'collection') {
      chain = chain.then(function () { return collectionProducts(cfg.collection).then(function (l) { l.forEach(function (raw) { if (need()) { var p = accept(raw); if (p) picked.push(p); } }); }); });
    } else {
      if (cfg.intent !== 'related') chain = chain.then(function () { return recs('complementary'); });
      chain = chain.then(function () { if (need()) return recs('related'); });
    }

    return chain.then(function () {
      if (cfg.mix.length) return dealMix();
      return collectionProducts(need() && cfg.intent !== 'collection' ? cfg.collection : '').then(function (l) {
        l.forEach(function (raw) { if (need()) { var p = accept(raw); if (p) picked.push(p); } });
        return picked;
      });
    }).then(function (final) {
      if (token !== self.token) return; /* a newer refresh is in flight */
      if (cfg.priorityTypes.length && !cfg.mix.length) {
        var rank = function (p) { var i = cfg.priorityTypes.indexOf(p.type.toLowerCase()); return i === -1 ? 999 : i; };
        final = final.map(function (p, i) { return { p: p, i: i }; })
          .sort(function (a, b) { return rank(a.p) - rank(b.p) || a.i - b.i; })
          .map(function (x) { return x.p; });
      }
      self.render(final.slice(0, cfg.limit));
    });
  };

  Block.prototype.render = function (products) {
    var self = this, cfg = this.cfg;
    var sameSet = products.length === this.shown.length && products.every(function (p, i) { return self.shown[i] === p.id; });
    if (sameSet) { this.root.hidden = !products.length; return; }
    this.shown = products.map(function (p) { return p.id; });
    if (!products.length) { this.root.hidden = true; this.list.innerHTML = ''; return; }
    this.say('');
    this.list.innerHTML = products.map(function (p) {
      var v0 = p.variants[0];
      var multi = p.variants.length > 1 && !(p.variants.length === 1 && v0.title === 'Default Title');
      var img = p.image
        ? '<a class="rca__img-link" href="' + esc(p.url) + '" tabindex="-1" aria-hidden="true"><img class="rca__img" src="' + esc(p.image) + '" alt="" width="150" height="150" loading="lazy" decoding="async"></a>'
        : '<span class="rca__img" aria-hidden="true"></span>';
      var select = '';
      if (multi) {
        select = '<select class="rca__variant" data-rca-variant aria-label="' + esc(cfg.strings.variantLabel.replace('{title}', p.title)) + '">' +
          p.variants.map(function (v) {
            return '<option value="' + v.id + '" data-price="' + v.price + '" data-compare="' + v.compare + '">' + esc(v.title) + '</option>';
          }).join('') + '</select>';
      }
      return '<li class="rca__item" data-rca-item data-product-id="' + p.id + '">' +
        img +
        '<div class="rca__body">' +
          '<a class="rca__name" href="' + esc(p.url) + '">' + esc(p.title) + '</a>' +
          '<p class="rca__price" data-rca-price>' + self.priceHTML(v0.price, v0.compare) + '</p>' +
          select +
        '</div>' +
        '<button type="button" class="rca__add" data-rca-add data-variant-id="' + v0.id + '" data-title="' + esc(p.title) + '" aria-label="' + esc(cfg.strings.addLabel.replace('{title}', p.title)) + '">' + esc(cfg.strings.add) + '</button>' +
      '</li>';
    }).join('');
    this.root.hidden = false;
    this.list.scrollLeft = 0;
    this.updateNav();
  };

  /* ---------- add to cart ---------- */
  Block.prototype.add = function (btn) {
    var self = this, cfg = this.cfg;
    if (btn.disabled) return;
    var variantId = parseInt(btn.getAttribute('data-variant-id'), 10);
    var title = btn.getAttribute('data-title') || '';
    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    btn.textContent = cfg.strings.adding;
    this.say('');

    /* window.fetch on purpose: the gifts section watches cart mutations through it and re-checks gift thresholds. */
    window.fetch(cfg.routes.add, {
      method: 'POST', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    }).then(function (r) { return r.json().then(function (j) { if (!r.ok) throw j; return j; }); })
      .then(function () {
        btn.textContent = cfg.strings.added;
        btn.classList.add('is-done');
        btn.removeAttribute('aria-busy');
        self.say(title + ' ' + cfg.strings.addedTo);
        /* keep focus in the block so keyboard users are not dropped */
        var next = btn.closest('[data-rca-item]').nextElementSibling;
        var target = next ? next.querySelector('[data-rca-add]') : self.root.querySelector('.rca__title');
        rebuildTheme(variantId).then(function () {
          if (target && (document.activeElement === btn || document.activeElement === document.body)) {
            if (target === self.root.querySelector('.rca__title')) target.setAttribute('tabindex', '-1');
            try { target.focus({ preventScroll: true }); } catch (e) {}
          }
        });
      })
      .catch(function (err) {
        console.warn('[Rhino add-ons] add failed', err);
        btn.disabled = false;
        btn.removeAttribute('aria-busy');
        btn.textContent = cfg.strings.retry;
        self.say((err && err.description) || cfg.strings.error, true);
      });
  };

  /* ---------- theme refresh ----------
     Ask Motion to rebuild (cart:build). Then verify the new line is on screen; if it is not,
     rebuild the cart lines, subtotal, discounts and header count ourselves from /cart?view=ajax. */
  function rebuildTheme(variantId) {
    document.dispatchEvent(new CustomEvent('cart:build'));
    return new Promise(function (resolve) {
      setTimeout(function () {
        var visible = Array.prototype.some.call(document.querySelectorAll('[data-products] [data-key]'), function (el) {
          return String(el.getAttribute('data-key') || '').indexOf(String(variantId) + ':') === 0;
        });
        if (visible) { resolve(); return; }
        manualRebuild().then(resolve, resolve);
      }, 1200);
    });
  }

  function manualRebuild() {
    var cfg = blocks[0] && blocks[0].cfg;
    if (!cfg) return Promise.resolve();
    return quietFetch(cfg.routes.cartAjax, { credentials: 'same-origin' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var items = doc.querySelector('.cart__items');
        var discounts = doc.querySelector('.cart__discounts');
        if (!items) return;
        var count = parseInt(items.getAttribute('data-count'), 10) || 0;
        var subtotal = parseInt(items.getAttribute('data-cart-subtotal'), 10) || 0;
        Array.prototype.forEach.call(document.querySelectorAll('[data-products]'), function (el) { el.innerHTML = items.innerHTML; });
        Array.prototype.forEach.call(document.querySelectorAll('[data-subtotal]'), function (el) { el.textContent = money(subtotal, cfg); });
        if (discounts) Array.prototype.forEach.call(document.querySelectorAll('[data-discounts]'), function (el) { el.innerHTML = discounts.innerHTML; });
        Array.prototype.forEach.call(document.querySelectorAll('[data-cart-count], .cart-link__bubble-num'), function (el) { el.textContent = count; });
        Array.prototype.forEach.call(document.querySelectorAll('.cart-link__bubble'), function (el) { el.classList.toggle('cart-link__bubble--visible', count > 0); });
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: { item_count: count } } }));
      })
      .catch(function (e) { console.warn('[Rhino add-ons] manual rebuild failed', e); });
  }

  /* ---------- wiring ---------- */
  var blocks = [];
  var timer = null;

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(sync, 250);
  }

  function sync() {
    if (!blocks.length) return;
    var cfg = blocks[0].cfg;
    getJSON(cfg.routes.cart).then(function (cart) {
      blocks.forEach(function (b) { b.refresh(cart); });
    }).catch(function (e) { console.warn('[Rhino add-ons] cart read failed', e); });
  }

  var wired = false;
  function init() {
    var cfg = readConfig();
    if (!cfg) return; /* section not added or switched off: containers stay hidden */
    Array.prototype.forEach.call(document.querySelectorAll('[data-rhino-cart-addons]:not([data-rca-init])'), function (root) {
      if (root.getAttribute('data-rca-location') === 'page' && cfg.showOnCartPage === false) return;
      root.setAttribute('data-rca-init', '1');
      blocks.push(new Block(root, cfg));
    });
    if (!blocks.length || wired) { if (blocks.length) sync(); return; }
    wired = true;
    ['cart:updated', 'cart:build', 'ajaxProduct:added', 'cart:change'].forEach(function (evt) {
      document.addEventListener(evt, schedule);
    });
    window.addEventListener('pageshow', function (e) { if (e.persisted) schedule(); });
    /* Theme editor: settings changes re-render sections, so pick up new blocks. */
    if (window.Shopify && window.Shopify.designMode) document.addEventListener('shopify:section:load', init);
    sync();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
